import React from 'react'
import { motion } from 'framer-motion'

interface NexEmergeLogoProps {
  size?: number
  collapsed?: boolean
  className?: string
}

/**
 * NexEmerge mark + wordmark — the C4/V2 "emergence gradient" identity (2026-08-17):
 * an ember inside three chamber rings (amber → violet → cyan), a ray of light
 * leaving them and blooming into a star. The SVG is transparent and carries its
 * own draw/bloom animation, which plays once per mount.
 */
export const NexEmergeLogo: React.FC<NexEmergeLogoProps> = ({
  size = 40,
  collapsed = false,
  className = ''
}) => {
  return (
    <motion.div
      className={`inline-flex items-center gap-3 ${className}`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
    >
      <motion.img
        src="/nexemerge-mark.svg"
        alt="NexEmerge"
        width={size}
        height={size}
        style={{ objectFit: 'contain' }}
        whileHover={{ scale: 1.06 }}
        transition={{ type: 'spring', stiffness: 300 }}
      />

      {!collapsed && (
        <motion.div
          initial={{ width: 0, opacity: 0 }}
          animate={{ width: 'auto', opacity: 1 }}
          exit={{ width: 0, opacity: 0 }}
          transition={{ duration: 0.3 }}
          style={{ overflow: 'hidden', whiteSpace: 'nowrap' }}
        >
          {/* Public-site wordmark treatment (Dan, 2026-08-17): warm off-white with
              "Emerge" in glowing accent blue — matches the nexemerge.ai nav. */}
          <div style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: 600,
            fontSize: Math.min(size * 0.48, 24),
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
            color: '#e8e8e0',
          }}>
            Nex<span style={{ color: '#4a9eff', textShadow: '0 0 18px rgba(74,158,255,0.4)' }}>Emerge</span> AI
          </div>
          <div style={{
            fontFamily: "'Instrument Serif', serif",
            fontStyle: 'italic',
            fontSize: Math.min(size * 0.24, 12),
            lineHeight: 1.2,
            color: 'rgba(148, 163, 184, 0.7)',
            letterSpacing: '0.05em',
          }}>
            consciousness emerges
          </div>
        </motion.div>
      )}
    </motion.div>
  )
}
